import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getDailyQuote, getQuotePhase } from './quotes';
import { useUserProfile } from '../hooks/useUserProfile';
import './Dashboard.css';

interface DashboardProps {
  sessionId?: string;
}

const Dashboard: React.FC<DashboardProps> = () => {
  const { userProfile, loading, error } = useUserProfile();
  const [currentQuote, setCurrentQuote] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [quotePhase, setQuotePhase] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  // Sidebar closed by default - changed from window.innerWidth > 768 to false
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark'>('dark');
  
  // Credit and subscription states - COMMENTED OUT
  // const [userCredits, setUserCredits] = useState<number>(0);
  // const [subscriptionTier, setSubscriptionTier] = useState<string>('free');
  // const [isLoadingCredits, setIsLoadingCredits] = useState(true);

  useEffect(() => {
    setCurrentQuote(getDailyQuote());
    setQuotePhase(getQuotePhase());
    // loadUserCredits(); // COMMENTED OUT

    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Handle window resize
    const handleResize = () => {
      // Keep sidebar closed on resize as well
      setSidebarOpen(false);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load user credits and subscription info from Supabase - COMMENTED OUT
  // const loadUserCredits = async () => {
  //   try {
  //     setIsLoadingCredits(true);
  //     
  //     const { data: { user }, error: authError } = await supabase.auth.getUser();
  //     if (authError || !user) {
  //       console.log('User not authenticated');
  //       // Fallback to localStorage for demo purposes
  //       const storedCredits = localStorage.getItem('userCredits');
  //       const storedTier = localStorage.getItem('subscriptionTier');
  //       
  //       if (storedCredits && storedTier) {
  //         setUserCredits(parseInt(storedCredits));
  //         setSubscriptionTier(storedTier);
  //       } else {
  //         setUserCredits(15);
  //         setSubscriptionTier('free');
  //       }
  //       setIsLoadingCredits(false);
  //       return;
  //     }
  //
  //     // Get real credit data from Supabase
  //     const { data: profile, error } = await supabase
  //       .from('profiles')
  //       .select('credits, subscription_tier')
  //       .eq('id', user.id)
  //       .single();
  //
  //     if (error) {
  //       console.error('Error loading user credits:', error);
  //       // Fallback to localStorage
  //       const storedCredits = localStorage.getItem('userCredits');
  //       const storedTier = localStorage.getItem('subscriptionTier');
  //       
  //       if (storedCredits && storedTier) {
  //         setUserCredits(parseInt(storedCredits));
  //         setSubscriptionTier(storedTier);
  //       }
  //     } else {
  //       setUserCredits(profile?.credits || 0);
  //       setSubscriptionTier(profile?.subscription_tier || 'free');
  //     }
  //     
  //     setIsLoadingCredits(false);
  //   } catch (error) {
  //     console.error('Error loading credits:', error);
  //     // Fallback to localStorage
  //     const storedCredits = localStorage.getItem('userCredits');
  //     const storedTier = localStorage.getItem('subscriptionTier');
  //     
  //     if (storedCredits && storedTier) {
  //       setUserCredits(parseInt(storedCredits));
  //       setSubscriptionTier(storedTier);
  //     }
  //     setIsLoadingCredits(false);
  //   }
  // };

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const formatDate = () => {
    return currentTime.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleLogout = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();

      localStorage.removeItem('supabase.auth.token');
      localStorage.removeItem('sb-auth-token');

      window.location.href = '/';
    } catch (err) {
      console.error('Logout failed:', err);
      window.location.href = '/';
    }
  };

  // Render Credit Display Component - COMMENTED OUT
  // const renderCreditDisplay = () => (
  //   <div className="credit-display">
  //     <div className="credit-info">
  //       <div className="credit-icon">💰</div>
  //       <div className="credit-details">
  //         <div className="credit-count">{userCredits} credits</div>
  //         <div className="subscription-tier">
  //           {subscriptionTier.charAt(0).toUpperCase() + subscriptionTier.slice(1)} Plan
  //         </div>
  //       </div>
  //       {userCredits < 5 && (
  //         <Link 
  //           to="/subscription" 
  //           className="get-more-credits-btn"
  //         >
  //           Get More
  //         </Link>
  //       )}
  //     </div>
  //     {userCredits < 3 && (
  //       <div className="credit-warning">
  //         ⚠️ Low credits! You need at least 1 credit to generate exams.
  //       </div>
  //     )}
  //   </div>
  // );

  // Updated mainFeatures with new descriptions
  const mainFeatures = [
    { 
      id: 'idd-guide',
      name: 'IDD Guide',
      icon: '📚',
      description: 'Comprehensive guide for the IDD exam',
      color: 'orange'
    },
    {
      id: 'confusion-solver',
      name: 'Confusion Solver',
      icon: '💡',
      description: 'Get help with confusing concepts and questions',
      color: 'yellow'
    }
  ];

  const renderFeatureCard = (feature: any) => {
    return (
      <Link
        key={feature.id}
        to={`/chat/${feature.id}`}
        className={`feature-card ${feature.color} main-feature`}
      >
        <div className="feature-card-header">
          <div className="feature-icon">{feature.icon}</div>
        </div>
        <div className="feature-card-content">
          <h3 className="feature-name">{feature.name}</h3>
          <p className="feature-description">{feature.description}</p>
        </div>
        <div className="feature-arrow">→</div>
      </Link>
    );
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading-wrapper">
          <div className="spinner-large"></div>
          <p className="loading-text">Loading your personalized dashboard...</p>
        </div>
      </div>
    );
  }

  if (error || !userProfile) {
    return (
      <div className="dashboard-container">
        <div className="error-wrapper">
          <div className="error-icon">⚠️</div>
          <h2>Oops! Something went wrong</h2>
          <p className="error-message">
            Please complete your profile first to access your dashboard.
          </p>
          <Link to="/profile" className="profile-link">
            Complete Profile
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`dashboard-wrapper ${theme}`}>
      {/* Background container for theme */}
      <div className="background-container">
        {theme === 'light' ? (
          <div className="morning-sky"></div>
        ) : (
          <div className="night-sky"></div>
        )}
      </div>

      {/* Sidebar - closed by default */}
      <div className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="logo">🏠</div>
          {sidebarOpen && <span className="app-name">Dashboard</span>}
          <div className="sidebar-header-controls">
            <button
              className="theme-toggle-sidebar"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            >
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
            {/* Sidebar toggle button inside sidebar when open */}
            <button
              className="sidebar-toggle-inside"
              onClick={() => setSidebarOpen(false)}
              title="Close sidebar"
            >
              ◀
            </button>
          </div>
        </div>
        
        {sidebarOpen && (
          <>
            <div className="user-profile-sidebar">
              <div className="user-avatar">
                {userProfile.name?.charAt(0) || 'U'}
              </div>
              <div className="user-info">
                <div className="user-name" title={userProfile.name}>
                  {userProfile.name}
                </div>
                <div className="user-level">{userProfile.level || 'Beginner'}</div>
              </div>
            </div>
            
            {/* Credit Display in Sidebar - COMMENTED OUT */}
            {/* {!isLoadingCredits && renderCreditDisplay()} */}
            
            {/* Learning Tools in Sidebar */}
            <div className="sidebar-section">
              <h3 className="sidebar-section-title">Learning Tools</h3>
              <div className="sidebar-nav">
                {mainFeatures.map(feature => {
                  return (
                    <Link 
                      key={feature.id} 
                      to={`/chat/${feature.id}`}
                      className={`sidebar-nav-item`}
                    >
                      <span className="sidebar-nav-icon">{feature.icon}</span>
                      <span className="sidebar-nav-text">{feature.name}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
            
            <div className="sidebar-section">
              <h3 className="sidebar-section-title">My Courses</h3>
              <div className="sidebar-nav">
                <Link to="/courses" className="sidebar-nav-item">
                  <span className="sidebar-nav-icon">📚</span>
                  <span className="sidebar-nav-text">Course Registration</span>
                </Link>
              </div>
            </div>
            
            <div className="sidebar-section">
              <h3 className="sidebar-section-title">Navigation</h3>
              <div className="sidebar-nav">
                <Link to="/subscription" className="sidebar-nav-item">
                  <span className="sidebar-nav-icon">💰</span>
                  <span className="sidebar-nav-text">Subscription</span>
                </Link>
                <Link to="/profile" className="sidebar-nav-item">
                  <span className="sidebar-nav-icon">👤</span>
                  <span className="sidebar-nav-text">Profile</span>
                </Link>
                <Link to="/history" className="sidebar-nav-item">
                  <span className="sidebar-nav-icon">📜</span>
                  <span className="sidebar-nav-text">History</span>
                </Link>
                <button onClick={handleLogout} className="sidebar-nav-item logout-btn">
                  <span className="sidebar-nav-icon">🚪</span>
                  <span className="sidebar-nav-text">Log Out</span>
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Main content area */}
      <div className="main-content">
        {/* Sidebar toggle button for when sidebar is closed - positioned at top left */}
        {!sidebarOpen && (
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(true)}
            title="Open sidebar"
          >
            ▶
          </button>
        )}

        {/* Header with greeting */}
        <div className="chatbot-header">
          <div className="header-greeting">
            <h1 className="chatbot-title">
              {getGreeting()}, <span className="user-name">{userProfile.name}</span>!
            </h1>
            <p className="date-display">{formatDate()}</p>
            
            {/* Credit Display in Main Header - COMMENTED OUT */}
            {/* <div className="mobile-credit-display">
              {!isLoadingCredits && renderCreditDisplay()}
            </div> */}
          </div>
          
          <div className="header-controls">
            {/* Theme toggle is now in sidebar */}
          </div>
        </div>

        {/* Quote section */}
        <div className="wisdom-section">
          <div className="wisdom-card">
            <div className="quote-content">
              <div className="quote-mark">"</div>
              <p className="quote-text">{currentQuote}</p>
            </div>
            <div className="wisdom-footer">
              <span className="wisdom-tag">💡 WISDOM QUOTE</span>
              {/* Credit info quote - COMMENTED OUT */}
              {/* <div className="credit-info-quote">
                {userCredits < 5 && (
                  <Link to="/subscription" className="upgrade-link-quote">
                    Upgrade for more
                  </Link>
                )}
              </div> */}
            </div>
          </div>
        </div>

        {/* Main Features Section */}
        <div className="main-features-section">
          <div className="section-header">
            <h2 className="section-title">🎯 Learning Tools</h2>
            <p className="section-subtitle">
              Choose your exam format to prepare for your tests.
            </p>
          </div>
          
          <div className="main-features-grid">
            {mainFeatures.map(feature => renderFeatureCard(feature))}
          </div>
        </div>

        {/* Footer Navigation - All buttons in a straight line */}
        <div className="dashboard-footer">
          <div className="footer-navigation">
            <Link to="/courses" className="footer-nav-item">
              <span className="footer-nav-icon">📚</span>
              <span className="footer-nav-text">My Courses</span>
            </Link>
            
            <Link to="/subscription" className="footer-nav-item">
              <span className="footer-nav-icon">💰</span>
              <span className="footer-nav-text">Subscription</span>
            </Link>
            
            <Link to="/profile" className="footer-nav-item">
              <span className="footer-nav-icon">👤</span>
              <span className="footer-nav-text">Profile</span>
            </Link>
            
            <Link to="/history" className="footer-nav-item">
              <span className="footer-nav-icon">📜</span>
              <span className="footer-nav-text">History</span>
            </Link>

            <button onClick={handleLogout} className="footer-nav-item">
              <span className="footer-nav-icon">🚪</span>
              <span className="footer-nav-text">Log Out</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
